import { useCallback, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useMutation, useQuery } from 'react-query';
import { Category, ExtractedCatalogueResponse, Product } from '../helpers/types';
import { ApiContext, ErrorObject } from '../../../../context/ApiContext';
import { API_V1_HOSTNAME_KEY } from '../../../../localStorageKeys';
import { skipSentry } from '../../../../helpers/helpers';

const useCatalogueImport = () => {
  const { apiFetch } = useContext(ApiContext);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [catalogueId, setCatalogueId] = useState<string>(null);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [fileNotAMenu, setFileNotAMenu] = useState(false);
  const [importedCategories, setImportedCategories] = useState<Category[]>(null);
  const [importedProducts, setImportedProducts] = useState<Product[]>(null);
  const [pollingRetries, setPollingRetries] = useState(10);

  const updateCatalogueCategories = useCallback(
    (newCategories: Category[]) => {
      setImportedCategories(newCategories);
    },
    [setImportedCategories]
  );
  const updateCatalogueProducts = useCallback(
    (newProducts: Product[]) => {
      setImportedProducts(newProducts);
    },
    [setImportedProducts]
  );
  const { mutate: importCatalogueFile } = useMutation(
    (body: FormData) =>
      apiFetch(
        `${localStorage.getItem(API_V1_HOSTNAME_KEY)}onboarding/proxy/ai-extract-catalog`,
        null,
        'POST',
        {},
        body
      ),
    {
      onSuccess(response) {
        setCatalogueId(response.id);
      },
      onError(error: ErrorObject) {
        setIsLoadingData(false);
        setShowErrorMessage(true);
        if (error?.code === 401) return;
        if (!skipSentry(error)) {
          Sentry.captureException(new Error(`Error creating extract catalogue in GPT.`), {
            extra: { error: JSON.stringify(error) },
          });
        }
      },
    }
  );

  const { refetch: pollImportStatus } = useQuery<ExtractedCatalogueResponse>(
    ['importedCatalogue'],
    () =>
      apiFetch(
        `${localStorage.getItem(
          API_V1_HOSTNAME_KEY
        )}onboarding/proxy/internal-ai-extract-catalog/${catalogueId}`
      ),
    {
      enabled: false,
      onSuccess(response: ExtractedCatalogueResponse) {
        switch (response?.status) {
          case 'SUCCESS':
            if (!response?.extractedCatalog?.categories && !response?.extractedCatalog?.products)
              setFileNotAMenu(true);
            setImportedCategories(response?.extractedCatalog?.categories || []);
            setImportedProducts(response?.extractedCatalog?.products || []);
            setIsLoadingData(false);
            break;
          case 'ERROR':
            setShowErrorMessage(true);
            setIsLoadingData(false);
            break;
          default:
            if (pollingRetries > 0) {
              setPollingRetries(pollingRetries - 1);
              setTimeout(pollImportStatus, 20000);
            } else {
              setShowErrorMessage(true);
              setIsLoadingData(false);
            }

            break;
        }
      },
      onError(error: ErrorObject) {
        setIsLoadingData(false);
        if (error?.code === 401) return;
        if (!skipSentry(error)) {
          Sentry.captureException(new Error('Error loading imported menu.'), {
            extra: { error: JSON.stringify(error) },
          });
        }
      },
    }
  );

  const { mutate: startCatalogueExtraction } = useMutation(
    () =>
      apiFetch(
        `${localStorage.getItem(
          API_V1_HOSTNAME_KEY
        )}onboarding/proxy/internal-ai-extract-catalog-start/${catalogueId}`,
        null,
        'POST',
        {}
      ),
    {
      onSuccess() {
        pollImportStatus();
      },
      onError(error: ErrorObject) {
        setIsLoadingData(false);
        setShowErrorMessage(true);
        if (error?.code === 401) return;
        if (!skipSentry(error)) {
          Sentry.captureException(new Error(`Error creating extract catalogue in GPT.`), {
            extra: { error: JSON.stringify(error) },
          });
        }
      },
    }
  );

  const callImportCatalogueFile = (file: File) => {
    if (!file) return;
    setFileNotAMenu(false);
    setShowErrorMessage(false);
    setIsLoadingData(true);
    setPollingRetries(10);
    const formdata = new FormData();
    formdata.append('file', file, file.name);
    importCatalogueFile(formdata);
  };

  useEffect(() => {
    if (catalogueId) {
      startCatalogueExtraction();
    }
  }, [catalogueId]);

  return {
    isLoadingData,
    showErrorMessage,
    fileNotAMenu,
    importedCategories,
    importedProducts,
    callImportCatalogueFile,
    updateCatalogueCategories,
    updateCatalogueProducts,
  };
};
export default useCatalogueImport;
